<template>
  <div class="card study-details">
    <div class="card-body">
      <div class="header">
        <div class="header-overlay"></div>
        <div class="left-container">
          <div class="title-wrapper">
            <div v-if="!edit" class="title">
              {{ metadata.study_info.project_name.value || "-" }}
            </div>
            <input
              v-else
              v-model="tagInputValues.project_name"
              type="text"
              placeholder="Project name"
              class="title-input data-input"
            />
          </div>
          <div class="company-wrapper">
            <div v-if="!edit" class="company">
              {{ displayAsMultivalueData(metadata.study_info.client.value) }}
              <div
                v-if="userPermissions.canAccessAnalytics"
                class="edit-label verified-label"
                :class="{ active: tagInputValues.verified }"
              >
                {{ `${tagInputValues.verified ? "Verified" : "Not verified"}` }}
              </div>
            </div>
            <el-select
              v-else
              class="select-style"
              v-model="tagInputValues.client"
              placeholder="Client"
              multiple
              filterable
              allow-create
              @keyup.native.esc="() => {}"
            >
              <el-option
                v-for="value in getOptionsForSelectedTag('client')"
                :key="value"
                :label="value"
                :value="value"
              ></el-option>
            </el-select>
            <div class="verify-toggle">
              <ToggleButton
                v-if="userPermissions.isAdmin && edit"
                class="toggle-switch "
                :width="37"
                :height="20"
                :sync="true"
                :color="{ checked: '#78BB95', unchecked: '#de1b1b' }"
                :disabled="!edit"
                v-model="tagInputValues.verified"
              >
              </ToggleButton>
              <div
                v-if="userPermissions.isAdmin && edit"
                class="edit-label verified-label"
                :class="{ active: tagInputValues.verified }"
              >
                {{
                  `${
                    tagInputValues.verified
                      ? "Validated"
                      : "Validation in progress"
                  }`
                }}
              </div>
            </div>
          </div>
        </div>
        <div class="right-container">
          <div class="id-numbers-wrapper">
            <div class="cmi-wrapper">
              <div v-if="!tagEditStatus.c_mi_number" class="cmi">
                <span style="margin-right: 10px;">CMI#</span>
                <span>{{ metadata.study_info.c_mi_number.value || "-" }}</span>
              </div>
              <input
                v-else
                v-model="tagInputValues.c_mi_number"
                type="text"
                placeholder="CMI#"
                class="cmi-input data-input"
              />
            </div>
            <div class="pace-wrapper">
              <div v-if="!edit" class="pace">
                <span style="margin-right: 10px;">Pace#</span>
                <span>{{ metadata.study_info.pace_number.value || "-" }}</span>
              </div>
              <input
                v-else
                v-model="tagInputValues.pace_number"
                type="text"
                placeholder="Pace#"
                class="pace-input data-input"
              />
            </div>
          </div>
          <div class="edit-toggle">
            <ToggleButton
              v-if="userPermissions.isAdmin"
              class="toggle-switch"
              :width="26"
              :height="14"
              :sync="true"
              @change="handleSwitchChange"
              :color="{ checked: '#78BB95', unchecked: '#4B4B4B' }"
              v-model="edit"
            ></ToggleButton>
            <div
              v-if="userPermissions.isAdmin"
              class="edit-label"
              :class="{ active: edit }"
            >
              Edit mode
            </div>
          </div>
          <div class="edit-toggle">
            <el-button
              v-if="edit && userPermissions.isAdmin"
              @click="() => $emit('showDetailsModal')"
              style="
                width: 150px;
                height: 29px;
                margin-top: 0;
                padding: 0;
                border-radius: 4px;
              "
              class="mr-2"
              type="primary"
            >
              <span class="ml-1 mr-2">Changes History</span>
            </el-button>
          </div>
        </div>
      </div>
      <div class="panels-wrapper">
        <div class="row">
          <div class="col-6 col-lg-3">
            <div class="data-panel">
              <div class="data-label">
                Business Unit
              </div>
              <div class="data-wrapper">
                <div v-if="!edit" class="data-value">
                  {{
                    getMultivalueText(
                      metadata.product_categorization.business_unit.value,
                      "business_unit"
                    )
                  }}
                </div>
                <el-select
                  v-else
                  v-model="tagInputValues.business_unit"
                  placeholder="Business Unit"
                  multiple
                  @keyup.native.esc="() => {}"
                >
                  <el-option
                    v-for="value in getOptionsForSelectedTag('product_area')"
                    :key="value"
                    :label="value"
                    :value="value"
                  ></el-option>
                </el-select>
              </div>
            </div>
          </div>
          <div class="col-6 col-lg-3">
            <div class="data-panel">
              <div class="data-label">
                Division
              </div>
              <div class="data-wrapper">
                <div v-if="!edit" class="data-value">
                  {{ metadata.product_categorization.division.value || "-" }}
                </div>
                <el-select
                  v-else
                  v-model="tagInputValues.division"
                  placeholder="Division"
                  @keyup.native.esc="() => {}"
                  filterable
                >
                  <el-option
                    v-for="value in getOptionsForSelectedTag('division')"
                    :key="value"
                    :label="value"
                    :value="value"
                  ></el-option>
                </el-select>
              </div>
            </div>
          </div>
          <div class="col-6 col-lg-3">
            <div class="data-panel">
              <div class="data-label">
                Category
              </div>
              <div class="data-wrapper">
                <div
                  v-if="!edit"
                  class="data-value clickable"
                  @click="toggleMultivalueField('category')"
                >
                  {{
                    getMultivalueText(
                      metadata.product_categorization.category.value,
                      "category"
                    )
                  }}
                </div>
                <el-select
                  v-else
                  v-model="tagInputValues.category"
                  placeholder="Category"
                  multiple
                  @keyup.native.esc="() => {}"
                  filterable
                >
                  <el-option
                    v-for="value in getOptionsForSelectedTag('category')"
                    :key="value"
                    :label="value"
                    :value="value"
                  ></el-option>
                </el-select>
              </div>
            </div>
          </div>
          <div class="col-6 col-lg-3">
            <div class="data-panel">
              <div class="data-label">
                Product Type
              </div>
              <div class="data-wrapper">
                <div
                  v-if="!edit"
                  class="data-value clickable"
                  @click="toggleMultivalueField('product_type')"
                >
                  {{
                    getMultivalueText(
                      metadata.product_categorization.product_type.value,
                      "product_type"
                    )
                  }}
                </div>
                <el-select
                  v-else
                  v-model="tagInputValues.product_type"
                  placeholder="Product Type"
                  multiple
                  @keyup.native.esc="() => {}"
                  filterable
                >
                  <el-option
                    v-for="value in getOptionsForSelectedTag('product_type')"
                    :key="value"
                    :label="value"
                    :value="value"
                  ></el-option>
                </el-select>
              </div>
            </div>
          </div>
        </div>
        <div class="row" style="margin-top: 30px">
          <div class="col-12 col-lg-3">
            <div class="geography-panel">
              <div class="geography-label">
                Geography
              </div>
              <div class="geography-wrapper">
                <div
                  class="geography-row"
                  :class="{
                    'incoherent--text': coherencyFails.includes('region'),
                  }"
                >
                  <div class="label">Region</div>
                  <div
                    v-if="!edit"
                    class="value clickable"
                    @click="toggleMultivalueField('region')"
                  >
                    {{
                      getMultivalueText(
                        metadata.geography.region.value,
                        "region"
                      )
                    }}
                  </div>
                  <el-select
                    v-else
                    v-model="tagInputValues.region"
                    placeholder="Region"
                    multiple
                    @keyup.native.esc="() => {}"
                    filterable
                  >
                    <el-option
                      v-for="value in getOptionsForSelectedTag('region')"
                      :key="value"
                      :label="value"
                      :value="value"
                    ></el-option>
                  </el-select>
                </div>
                <div class="geography-row">
                  <div class="label">Lead Country</div>
                  <div v-if="!edit" class="value">
                    {{
                      getMultivalueText(metadata.geography.lead_country.value)
                    }}
                  </div>
                  <el-select
                    v-else
                    v-model="tagInputValues.lead_country"
                    placeholder="Lead country"
                    @keyup.native.esc="() => {}"
                    filterable
                  >
                    <el-option
                      v-for="value in getOptionsForSelectedTag('country')"
                      :key="value"
                      :label="value"
                      :value="value"
                    ></el-option>
                  </el-select>
                </div>
                <div
                  class="geography-row"
                  :class="{
                    'incoherent--text': coherencyFails.includes('country'),
                  }"
                >
                  <div class="label">Country</div>
                  <div
                    v-if="!edit"
                    class="value clickable"
                    @click="toggleMultivalueField('country')"
                  >
                    {{
                      getMultivalueText(
                        metadata.geography.country.value,
                        "country"
                      )
                    }}
                  </div>
                  <el-select
                    v-else
                    v-model="tagInputValues.country"
                    placeholder="Country"
                    multiple
                    @keyup.native.esc="() => {}"
                    filterable
                  >
                    <el-option
                      v-for="value in getOptionsForSelectedTag('country')"
                      :key="value"
                      :label="value"
                      :value="value"
                    ></el-option>
                  </el-select>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12 col-lg-9">
            <div class="methodological-details">
              <div class="label">
                Methodological Details
              </div>
              <div class="wrapper">
                <div class="main-data">
                  <div class="data-row">
                    <div
                      class="data"
                      :class="{
                        'incoherent--text': coherencyFails.includes(
                          'methodology'
                        ),
                      }"
                    >
                      <div class="data-header">Methodology</div>
                      <div class="data-value">
                        <span v-if="!edit">{{
                          displayAsMultivalueData(
                            metadata.methodological_details_1.methodology.value
                          )
                        }}</span>
                        <el-select
                          v-else
                          v-model="tagInputValues.methodology"
                          placeholder="Methodology"
                          multiple
                          @keyup.native.esc="() => {}"
                          filterable
                        >
                          <el-option
                            v-for="value in getOptionsForSelectedTag(
                              'methodology'
                            )"
                            :key="value"
                            :label="value"
                            :value="value"
                          ></el-option>
                        </el-select>
                      </div>
                    </div>
                    <div class="data">
                      <div class="data-header">Design Method</div>
                      <div class="data-value">
                        <span v-if="!edit">{{
                          displayAsMultivalueData(
                            metadata.methodological_details_3.design_method.value
                          )
                        }}</span>
                        <el-select
                          v-else
                          v-model="tagInputValues.design_method"
                          placeholder="Design Method"
                          multiple
                          @keyup.native.esc="() => {}"
                          filterable
                        >
                          <el-option
                            v-for="value in getOptionsForSelectedTag(
                              'design_method'
                            )"
                            :key="value"
                            :label="value"
                            :value="value"
                          ></el-option>
                        </el-select>
                      </div>
                    </div>
                    <div class="data">
                      <div class="data-header">Base #</div>
                      <div class="data-value">
                        <span v-if="!edit">{{
                          metadata.methodological_details_4.base.value || "-"
                        }}</span>
                        <input
                          v-else
                          v-model="tagInputValues.base"
                          type="text"
                          class="methodological-input data-input"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="data-row" style="margin-top: 18px">
                    <div class="data">
                      <div class="data-header">Gender</div>
                      <div class="data-value">
                        <span v-if="!edit">{{
                          metadata.methodological_details_2.gender.value || "-"
                        }}</span>
                        <el-select
                          v-else
                          v-model="tagInputValues.gender"
                          placeholder="Gender"
                          @keyup.native.esc="() => {}"
                          filterable
                        >
                          <el-option
                            v-for="value in getOptionsForSelectedTag('gender')"
                            :key="value"
                            :label="value"
                            :value="value"
                          ></el-option>
                        </el-select>
                      </div>
                    </div>
                    <div class="data">
                      <div class="data-header">Age</div>
                      <div class="data-value">
                        <span v-if="!edit">{{
                          metadata.methodological_details_2.age.value || "-"
                        }}</span>
                        <input
                          v-else
                          v-model="tagInputValues.age"
                          type="text"
                          class="methodological-input data-input"
                        />
                      </div>
                    </div>
                    <div class="data">
                      <div class="data-header">Social class</div>
                      <div class="data-value">
                        <span v-if="!edit">{{
                          metadata.methodological_details_2.social_class
                            .value || "-"
                        }}</span>
                        <input
                          v-else
                          v-model="tagInputValues.social_class"
                          type="text"
                          class="methodological-input data-input"
                        />
                      </div>
                    </div>
                    <div class="data">
                      <div class="data-header">Study sample size</div>
                      <div class="data-value">
                        <span v-if="!edit">{{
                          metadata.methodological_details_2.study_sample_size
                            .value || "-"
                        }}</span>
                        <input
                          v-else
                          v-model="tagInputValues.study_sample_size"
                          type="text"
                          class="methodological-input data-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div class="panel-profile">
                  <div class="data">
                    <div class="data-header">Panel Profile</div>
                    <div class="data-value">
                      <span v-if="!edit">{{
                        metadata.methodological_details_1.panel_profile.value ||
                          "-"
                      }}</span>
                      <input
                        v-else
                        v-model="tagInputValues.panel_profile"
                        type="text"
                        class="methodological-input data-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row details-links" style="margin-top: 30px;">
          <div class="col-12 d-flex justify-content-end align-items-center">
            <a href="#fragrance" class="main-category">
              <font-awesome-icon icon="vial"></font-awesome-icon>
              <span style="margin-left: 5px">Fragrance Details</span>
            </a>
            <a href="#files" class="main-category ml-3">
              <font-awesome-icon icon="file-alt"></font-awesome-icon>
              <span style="margin-left: 5px">Files related to the study</span>
            </a>
            <router-link
              v-if="questions.length"
              :to="'/study/' + $route.params.id + '/mapping/'"
              class="main-category ml-3"
            >
              <font-awesome-icon icon="table"></font-awesome-icon>
              <span style="margin-left: 5px">Mapping to questions</span>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ToggleButton from '@/components/shared/ToggleButton'
import { mapGetters, mapActions } from 'vuex'
import studyGetters from '../../store/modules/studies/getterTypes'
import _ from 'lodash'
import studyActions from '../../store/modules/studies/actionTypes'
import globalAlertMixin from '../../mixins/globalAlert'
import generalActions from '../../store/modules/general/actionTypes'
import generalGetters from '../../store/modules/general/getterTypes'

export default {
  name: 'DetailsCard',
  components: { ToggleButton },
  mixins: [globalAlertMixin],
  props: {
    coherencyFails: Array
  },
  data () {
    return {
      edit: false,
      dataCopy: {},
      tagEditStatus: {
        project_name: false,
        client: false,
        c_mi_number: false,
        pace_number: false,
        business_unit: false,
        division: false,
        category: false,
        product_type: false,
        region: false,
        lead_country: false,
        country: false,
        methodology: false,
        design_method: false,
        base: false,
        gender: false,
        age: false,
        social_class: false,
        study_sample_size: false,
        panel_profile: false,
        verified: false
      },
      visibleMultivalueFields: [],
      oldTagInputValues: {},
      tagInputValues: {
        project_name: '',
        client: '',
        c_mi_number: '',
        pace_number: '',
        business_unit: '',
        division: '',
        category: '',
        product_type: '',
        region: '',
        lead_country: '',
        country: '',
        methodology: '',
        design_method: '',
        base: '',
        gender: '',
        age: '',
        social_class: '',
        study_sample_size: '',
        panel_profile: '',
        verified: false
      },
      multiselectFields: [
        'client',
        'business_unit',
        'category',
        'product_type',
        'methodology',
        'design_method',
        'country',
        'region'
      ]
    }
  },
  computed: {
    ...mapGetters({
      metadata: studyGetters.GET_STUDY_DETAILS_METADATA,
      questions: studyGetters.GET_QUESTIONS,
      tags: studyGetters.GET_STUDY_DETAILS_TAGS,
      userPermissions: generalGetters.GET_PERMISSIONS
    })
  },
  methods: {
    ...mapActions({
      toggleLoading: generalActions.TOGGLE_LOADING,
      editTag: studyActions.POST_TAG_EDIT
    }),
    displayAsMultivalueData (value) {
      if (!value || !value.length) {
        return ''
      }
      if (typeof value === 'string') {
        return value
      }
      return value.join(', ')
    },
    getOptionsForSelectedTag (tag) {
      const selectedTag = this.tags.find((el) => el.field === tag)
      return selectedTag ? selectedTag.options : []
    },
    getMultivalueText (filterObj, key) {
      let obj =
        key === 'business_unit'
          ? filterObj && filterObj.length
            ? filterObj
            : this.metadata.product_categorization.product_area.value
          : filterObj
      if (!obj || !obj.length) {
        return '-'
      }
      if (typeof obj === 'string') {
        return obj
      }
      const value = _.clone(obj)
      if (value.length < 3) {
        return value.join(', ')
      } else {
        return this.visibleMultivalueFields.includes('country')
          ? value.join(', ')
          : value.splice(0, 2).join(', ') + ' ...'
      }
    },
    toggleMultivalueField (key) {
      if (this.visibleMultivalueFields.includes(key)) {
        this.visibleMultivalueFields.splice(
          this.visibleMultivalueFields.indexOf(key),
          1
        )
      } else {
        this.visibleMultivalueFields.push(key)
      }
    },
    flattenData (data) {
      const flattenedData = {}
      Object.keys(data).forEach((key) => {
        _.merge(flattenedData, data[key])
      })
      return flattenedData
    },
    saveDataCopy (data) {
      this.dataCopy = _.cloneDeep(data)
      const flatData = this.flattenData(this.dataCopy)
      Object.keys(flatData).forEach((key) => {
        if (Object.keys(this.tagInputValues).includes(key)) {
          this.oldTagInputValues[key] = flatData[key].value
        }
      })
    },
    convertStringIntoArrayOfStrings (value) {
      if (typeof value === 'string') {
        if (value.includes(',')) return value.split(',')
        else return [value]
      } else if (Array.isArray(value)) {
        return value
      }
    },
    fillInputs (data) {
      this.dataCopy = _.cloneDeep(data)
      const flatData = this.flattenData(this.dataCopy)
      Object.keys(flatData).forEach((key) => {
        if (this.multiselectFields.includes(key)) {
          if (['product_area', 'business_unit'].includes(key)) {
            this.tagInputValues.business_unit = this.convertStringIntoArrayOfStrings(flatData.product_area.value)
          } else {
            this.tagInputValues[key] = this.convertStringIntoArrayOfStrings(flatData[key].value)
          }
        } else if (key === 'gender') {
          if (Array.isArray(flatData[key].value)) this.tagInputValues[key] = flatData[key].value[0]
          else this.tagInputValues[key] = flatData[key].value
        } else if (Object.keys(this.tagInputValues).includes(key)) {
          this.tagInputValues[key] = flatData[key].value
        }
      })
    },
    async handleSwitchChange ({ value }) {
      if (!value) {
        await this.saveEdit()
      } else {
        this.saveDataCopy(this.metadata)
        this.fillInputs(this.metadata)
        this.handleIncoherentData([])
      }
    },
    cancelEdit () {
      this.edit = false
      this.tagInputValues = { ...this.oldTagInputValues }
    },
    handleIncoherentData (data) {
      this.$emit('incoherent-data', data)
    },
    async saveEdit () {
      this.toggleLoading(true)
      this.edit = false
      const data = {}
      const oldData = {}
      Object.keys(this.tagInputValues).forEach((tag) => {
        if (!_.isEqual(this.tagInputValues[tag], this.oldTagInputValues[tag])) {
          if (tag === 'gender') {
            data[tag] = [this.tagInputValues[tag]]
          } else {
            data[tag] = this.tagInputValues[tag]
            oldData[tag] = this.oldTagInputValues[tag]
          }
        }
      })
      const res = await this.editTag({
        studyId: this.$route.params.id,
        data: { study: data, oldData, type: 'study' }
      })
      this.$emit('update')
      let options
      if (!res) {
        this.tagInputValues = { ...this.oldTagInputValues }
        options = {
          duration: 5000,
          title: 'Failed!',
          message:
            'There was an issue with your request, please try again later.',
          type: 'success'
        }
      } else if (res.status === 400) {
        if (res.data && res.data.incoherent_fields) {
          options = {
            duration: 5000,
            title: 'Failed!',
            message: `${res.data.message}. Bad fields: ${res.data.incoherent_fields}`,
            type: 'error'
          }
          this.handleIncoherentData(res.data.incoherent_fields)
        } else {
          this.$emit('error', res)
          options = {
            duration: 5000,
            title: 'Failed!',
            message:
              'There was an issue with your request, please try again later.',
            type: 'error'
          }
        }
      } else {
        this.$emit('update')
        options = {
          duration: 5000,
          title: 'Success!',
          message: 'Study data was updated successfully.',
          type: 'success'
        }
      }
      this.launchAlert(options)
      this.toggleLoading(false)
      window.location.reload()
    }
  },
  async mounted () {
    if (this.metadata) {
      this.saveDataCopy(this.metadata)
      this.fillInputs(this.metadata)
    }

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.edit) {
        this.cancelEdit()
      }
      if (e.key === 'Enter' && this.edit) {
        this.saveEdit()
      }
    })
  }
}
</script>

<style lang="scss" scoped>
@import "../../assets/scss/variables";

.clickable {
  cursor: pointer;
}

.incoherent {
  &--text {
    * {
      color: #e34850 !important;
    }
  }
}

.study-details {
  width: 100%;
  border: 0;
  background-color: #f8f8f8;
  margin-bottom: 35px;

  .card-body {
    padding: 0 0 30px 0;

    .data-input {
      border: none;
      background-color: transparent;
    }

    .header {
      position: relative;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      height: 170px;
      background: transparent
        linear-gradient(
          266deg,
          rgba(115, 178, 224, 0.73) 0%,
          rgba(0, 39, 204, 0.73) 100%
        )
        0 0 no-repeat padding-box;
      box-shadow: 0 3px 6px #00000029;
      border: 1px solid #231a44;
      margin-bottom: 30px;

      .header-overlay {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-image: url("../../assets/images/headers/header-wave.svg");
        opacity: 0.73;
        background-size: cover;
      }

      .data-input {
        border: none;
        border-bottom: 2px solid white;
        background-color: transparent;
        color: white;

        &::placeholder {
          color: rgba(255, 255, 255, 0.6);
        }
      }

      .left-container {
        z-index: 2;
        height: 100%;
        padding: 13px 0 8px 34px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-start;

        .title-wrapper {
          height: 56px;

          .title {
            text-transform: uppercase;
            font-family: "Source Sans Pro", sans-serif;
            font-weight: 900;
            font-size: 45px;
            line-height: 56px;
            letter-spacing: 0.9px;
            color: white;
          }

          .title-input {
            font-family: "Source Sans Pro", sans-serif;
            font-weight: 900;
            height: 56px;
            font-size: 45px;
          }
        }

        .company-wrapper {
          height: 30px;

          .select-style {
            ::v-deep .el-select__input {
              color: white
            }
          }

          .company {
            text-transform: uppercase;
            font-family: "Open Sans", sans-serif;
            font-weight: 900;
            font-size: 22px;
            line-height: 30px;
            letter-spacing: 0;
            color: white;
          }
          .verified-label {
            font-size: 12px;
            color: #4b4b4b;
            display: inline-block;
            &.active {
              color: #78bb95;
            }
          }
          .verify-toggle {
            display: inline-block;
            label {
              margin: 0 15px;
            }
          }
          .company-input {
            height: 30px;
            font-size: 22px;
          }
        }
      }

      .right-container {
        padding: 13px 53px 13px 0;
        z-index: 2;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-end;

        .id-numbers-wrapper {
          width: 230px;
          height: 59px;
          display: flex;
          flex-direction: column;
          align-items: flex-end;

          .cmi-wrapper {
            height: 23px;

            .cmi {
              font-family: "Source Sans Pro", sans-serif;
              font-weight: 700;
              font-size: 18px;
              line-height: 23px;
              letter-spacing: 0;
              color: white;
              text-align: right;
              span {
                white-space: nowrap;
              }
            }

            .cmi-input {
              font-size: 18px;
              height: 23px;
            }
          }

          .pace-wrapper {
            height: 23px;
            margin-top: 5px;

            .pace {
              font-family: "Source Sans Pro", sans-serif;
              font-weight: 700;
              font-size: 18px;
              line-height: 23px;
              letter-spacing: 0;
              color: white;
              text-align: right;
            }

            .pace-input {
              font-family: "Source Sans Pro", sans-serif;
              font-weight: 700;
              font-size: 18px;
              height: 23px;
            }
          }
        }

        .edit-toggle {
          height: 32px;
          width: 100px;
          display: flex;
          justify-content: center;
          align-items: center;

          .toggle-switch {
            margin: 0 10px 0 0;
          }

          .edit-label {
            font-family: "Source Sans Pro", sans-serif;
            font-size: 14px;
            line-height: 18px;
            color: #4b4b4b;
            transition: color 0.2s linear;

            &.active {
              color: #78bb95;
            }
          }
        }
      }
    }

    .panels-wrapper {
      margin: 0 15px;

      .details-links {
        a {
          color: #4d4f5c;
          font-family: "Source Sans Pro", sans-serif;
          font-size: 18px;
          line-height: 25px;
        }
      }

      .data-input {
        border: none;
        border-bottom: 2px solid #4d4f5c;
        background-color: transparent;
        color: #4d4f5c;

        &::placeholder {
          color: rgba(67, 66, 93, 0.5);
        }
      }

      .row {
        margin: 0;
        width: 100%;
        justify-content: center;

        .data-panel {
          padding: 22px 21px;
          height: 100%;
          background: #ffffff 0 0 no-repeat padding-box;
          box-shadow: 0 2px 6px #0000000a;
          display: flex;
          flex-direction: column;

          .data-label {
            height: 20px;
            font-family: "Source Sans Pro", sans-serif;
            font-size: 16px;
            line-height: 27px;
            color: #43425d;
            opacity: 0.5;
          }

          .data-wrapper {
            @media screen and (min-width: $break-1080p) {
              margin-top: 32px;
            }
            @media screen and (max-width: $break-1080p) {
              margin-top: 26px;
            }
            font-family: "Source Sans Pro", sans-serif;
            font-weight: 700;
            font-size: 27px;
            line-height: 34px;
            color: #4d4f5c;

            .data-input {
              height: 34px;
            }
          }
        }

        .geography-panel {
          padding: 22px 21px;
          height: 100%;
          background: #ffffff 0 0 no-repeat padding-box;
          box-shadow: 0 2px 6px #0000000a;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          background-image: url("../../assets/images/study-details/world-map.png");
          background-position: center;
          background-size: contain;

          .geography-label {
            height: 20px;
            font-family: "Source Sans Pro", sans-serif;
            font-size: 16px;
            line-height: 27px;
            color: #43425d;
          }

          .geography-wrapper {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            margin-bottom: 23px;
            margin-left: 16px;

            .geography-row {
              display: flex;
              flex-wrap: wrap;
              align-items: center;

              .label {
                width: 170px;
                font-family: "Source Sans Pro", sans-serif;
                font-weight: 700;
                font-size: 24px;
                line-height: 32px;
                color: #4d4f5c;
                text-align: left;
                margin-right: 30px;
              }

              .value {
                font-family: "Source Sans Pro", sans-serif;
                font-size: 24px;
                line-height: 32px;
                color: #4d4f5c;
                text-align: left;
                max-width: 240px;
                width: 100%;
              }

              .geography-input {
                height: 34px;
              }
            }
          }
        }

        .methodological-details {
          padding: 22px 21px;
          min-height: 311px;
          background: #ffffff 0 0 no-repeat padding-box;
          box-shadow: 0 2px 6px #0000000a;
          display: flex;
          flex-direction: column;

          .label {
            height: 20px;
            font-family: "Source Sans Pro", sans-serif;
            font-size: 16px;
            line-height: 27px;
            color: #43425d;
          }

          .wrapper {
            width: 100%;
            display: flex;
            margin-top: 22px;
            padding: 0 !important;

            .main-data {
              width: 66%;
              display: flex;
              flex-direction: column;
            }

            .panel-profile {
              width: calc(34% - 16px);
              margin-left: 16px;

              .data {
                flex: 1;
                background: #ffffff 0 0 no-repeat padding-box;

                .data-header {
                  text-transform: uppercase;
                  font-weight: 700;
                  height: 50px;
                  background: #f5f6fa 0% 0% no-repeat padding-box;
                  font-family: "Source Sans Pro", sans-serif;
                  font-size: 14px;
                  line-height: 18px;
                  color: #a3a6b4;
                  display: flex;
                  justify-content: flex-start;
                  align-items: center;
                  padding: 16px 22px;
                }

                .data-value {
                  background: #ffffff 0% 0% no-repeat padding-box;
                  min-height: 50px;
                  font-family: "Source Sans Pro", sans-serif;
                  font-size: 18px;
                  line-height: 25px;
                  color: #4d4f5c;
                  display: flex;
                  justify-content: flex-start;
                  align-items: center;
                  padding: 14px 90px 14px 10px;

                  input {
                    height: 34px;
                    width: 100%;
                  }
                }
              }
            }

            .data-row {
              width: 100%;
              display: flex;
              background: #ffffff 0% 0% no-repeat padding-box;

              .data {
                flex: 1;
                background: #ffffff 0 0 no-repeat padding-box;
                border-bottom: 1px solid #f1f1f3;

                .data-header {
                  text-transform: uppercase;
                  font-weight: 700;
                  height: 50px;
                  background: #f5f6fa 0% 0% no-repeat padding-box;
                  font-family: "Source Sans Pro", sans-serif;
                  font-size: 14px;
                  line-height: 18px;
                  color: #a3a6b4;
                  display: flex;
                  justify-content: flex-start;
                  align-items: center;
                  padding: 16px 22px;
                }

                .data-value {
                  background: #ffffff 0% 0% no-repeat padding-box;
                  min-height: 50px;
                  font-family: "Source Sans Pro", sans-serif;
                  font-size: 27px;
                  line-height: 34px;
                  color: #4d4f5c;
                  display: flex;
                  justify-content: flex-start;
                  align-items: center;
                  padding: 3px 0 13px 20px;

                  input {
                    height: 34px;
                    max-width: 180px;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
@import "../../assets/scss/variables";

.study-details {
  .el-select {
    .el-input {
      input {
        border-top: 0 !important;
        border-right: 0 !important;
        border-left: 0 !important;
        border-radius: 0 !important;
        background-color: transparent;
        padding: 0 15px 0 0 !important;
      }
    }
  }

  .header {
    .el-select {
      .el-input {
        input {
          border-bottom: 2px solid white !important;
          font-family: "Open Sans", sans-serif;
          font-weight: 900;
          font-size: 22px;
          line-height: 30px;
          letter-spacing: 0;
          color: white;
          height: 30px;
        }
      }
    }
  }

  .data-panel {
    .el-select {
      .el-input {
        input {
          border-bottom: 2px solid #4d4f5c !important;
          height: 34px;
          font-family: "Source Sans Pro", sans-serif;
          font-weight: 700;
          font-size: 27px;
          line-height: 34px;
          color: #4d4f5c;
        }
      }
    }
  }

  .methodological-details {
    .el-select {
      .el-input {
        input {
          border-bottom: 2px solid #4d4f5c !important;
          height: 34px;
          font-family: "Source Sans Pro", sans-serif;
          font-size: 27px;
          line-height: 34px;
          color: #4d4f5c;
          max-width: 180px;
        }
      }
    }
  }
}
</style>
